import { List as ImmutableList, OrderedMap, type Set } from 'immutable';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector } from 'reselect';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from 'app-components/flux/accounts';
import * as categoriesSelectors from 'app-components/flux/categories/categoriesSelectors';
import type { CATEGORIES_REDUCER_KEY, CategoryProps } from 'app-components/flux/categories/categoriesTypes';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getCategoriesById } from '../categories/categoriesSelectors';
import * as chartOfAccountsTypes from './chartOfAccountsTypes';
import * as chartOfAccountsUtils from './chartOfAccountsUtils';

export const getChartOfAccountsTree = createSelector(
  categoriesSelectors.getCategoriesIdsByUsageType,
  accountsSelectors.getAccountsById,
  accountsSelectors.getGoalAccountIds,
  buildCoaTree,
);

export const getChartOfAccountForKnownCatId = createSelector(
  [getCategoriesById, (_s, knownId: string) => knownId],
  (categories, knownId) => {
    const catId = categories.find((category) => category.knownCategoryId === knownId)?.id;
    // Works best for known cats that are non-editable on QCS side. Eg Business income. Will return uncat as backup.
    return catId
      ? chartOfAccountsTypes.mkChartOfAccount({ type: 'CATEGORY', id: catId })
      : chartOfAccountsTypes.mkChartOfAccount({ type: 'UNCATEGORIZED', id: '0' });
  },
);

export const getChartOfAccountCategoriesTree = createSelector(getChartOfAccountsTree, (coaTree) =>
  coaTree.filter(
    (coaNode) =>
      coaNode.coa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
      coaNode.coa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY,
  ),
);

// Currently, this assumes that all the category usage-type nodes are at the root level
// and that only one node exists for each type.
export const getChartOfAccountCategoryUsageTypeNodes = createSelector(
  getChartOfAccountCategoriesTree,
  (coaCategoriesTree) =>
    coaCategoriesTree.filter((coaNode) => coaNode.coa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE),
);

type CoaByParentIdObject = {
  id?: string;
  coa: chartOfAccountsTypes.ChartOfAccountProps;
  usageType?: string;
  name?: string;
  inList?: boolean;
};

type CoasByParentId = Map<string, CoaByParentIdObject[]>;

function buildCoaTree(
  categoriesByIdByUsageType: ReturnType<typeof categoriesSelectors.getCategoriesIdsByUsageType>,
  accountsById: ReturnType<typeof accountsSelectors.getAccountsById>,
  goalAccountIds: ReturnType<typeof accountsSelectors.getGoalAccountIds>,
): ImmutableList<chartOfAccountsTypes.ChartOfAccountNode> {
  if (!categoriesByIdByUsageType || categoriesByIdByUsageType.size === 0) {
    return ImmutableList();
  }

  const coasByParentId: CoasByParentId = new Map();
  coasByParentId.set('0', []);

  const enableUsageTypes = (categoriesByIdByUsageType?.size || 0) > 1;
  if (enableUsageTypes) {
    categoriesByIdByUsageType.forEach((categories, usageType) => {
      const id = `USAGE-${usageType}`;
      const name = `${usageType.charAt(0).toUpperCase() + usageType.slice(1).toLowerCase()}`;
      coasByParentId.get('0')?.push({
        id,
        coa: { type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE, id: usageType },
        usageType,
        name,
      });
      addCoaCategories(categories, id, coasByParentId);
    });
  } else {
    categoriesByIdByUsageType.valueSeq().forEach((categories) => addCoaCategories(categories, '0', coasByParentId));
  }

  addCoaTransfers(enableUsageTypes, accountsById, goalAccountIds, coasByParentId);
  addCoaOthers(enableUsageTypes, coasByParentId);
  // Add uncategorized at the top level
  const uncategorizedCoa = { type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED, id: '0' };
  coasByParentId.get('0')?.push({
    id: 'UNCATEGORIZED',
    coa: uncategorizedCoa,
    usageType: chartOfAccountsTypes.CoaUsageTypeEnum.OTHER,
    name: 'Uncategorized',
  });

  // sort the categories for each parent
  coasByParentId.forEach((categories, parentId) => {
    if (!enableUsageTypes || parentId !== '0') {
      categories.sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '', undefined, { numeric: true, sensitivity: 'base' }),
      );
    }
  });

  return ImmutableList(
    coasByParentId.get('0')?.map((data) => {
      const children = coasByParentId.get(data.id || '');
      return createCoaNode(enableUsageTypes, coasByParentId, [], '', data, children);
    }),
  );
}

function addCoaCategories(
  categoriesByIds: Immutable.Collection<number, Immutable.RecordOf<CategoryProps>>,
  rootId: string,
  coasByParentId: CoasByParentId,
) {
  categoriesByIds.reduce((map, category) => {
    const parentId = category.parentId && category.parentId !== '0' ? category.parentId : rootId;
    map.set(parentId, map.get(parentId) || []);

    const coa = { type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY, id: getId(category) };
    map.get(parentId)?.push({
      id: chartOfAccountsUtils.getCoaId(coa),
      coa,
      usageType: category.usageType,
      name: category.name,
    });

    return map;
  }, coasByParentId);
}

function addCoaOthers(enableUsageTypes: boolean, coasByParentId: CoasByParentId) {
  let otherRootNode: Array<CoaByParentIdObject> = [];
  if (enableUsageTypes) {
    const coa = { type: chartOfAccountsTypes.CoaTypeEnum.DISPLAY_NODE, id: '0' };
    coasByParentId.get('0')?.push({
      id: 'OTHER',
      coa,
      usageType: chartOfAccountsTypes.CoaUsageTypeEnum.OTHER,
      name: 'Other',
    });
    coasByParentId.set('OTHER', (otherRootNode = []));
  } else {
    otherRootNode = coasByParentId.get('0') || [];
  }

  // add balance adjustment chart of accounts
  //
  const baCoa = { type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT, id: '0' };
  otherRootNode.push({
    id: 'BALANCE_ADJUSTMENT_0',
    coa: baCoa,
    usageType: chartOfAccountsTypes.CoaUsageTypeEnum.OTHER,
    name: chartOfAccountsUtils.getBalanceAdjustmentString(baCoa.id), // 'Balance Adjustment',
  });

  // add balance adjustment (opening-balance) chart of accounts
  //
  const obCoa = { type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT, id: '1' };
  otherRootNode.push({
    id: 'BALANCE_ADJUSTMENT_1',
    coa: obCoa,
    usageType: chartOfAccountsTypes.CoaUsageTypeEnum.OTHER,
    name: chartOfAccountsUtils.getBalanceAdjustmentString(obCoa.id), // 'Opening Balance',
  });

  // add balance adjustment (credit-card payment) chart of accounts
  //
  const ccPaymentCoa = { type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT, id: '3' };
  otherRootNode.push({
    id: 'BALANCE_ADJUSTMENT_3',
    coa: ccPaymentCoa,
    usageType: chartOfAccountsTypes.CoaUsageTypeEnum.OTHER,
    name: chartOfAccountsUtils.getBalanceAdjustmentString(ccPaymentCoa.id),
  });
}

// add transfers (accounts and balance adjustment (external transfer)) chart of accounts
//
function addCoaTransfers(
  enableUsageTypes: boolean,
  accountsById: ReturnType<typeof accountsSelectors.getAccountsById>,
  hiddenAccountIds: Set<string>,
  coasByParentId: CoasByParentId,
) {
  const transfers: Array<CoaByParentIdObject> = [];
  if (enableUsageTypes) {
    const coa = { type: chartOfAccountsTypes.CoaTypeEnum.DISPLAY_NODE, id: '0' };
    coasByParentId.get('0')?.push({
      id: 'TRANSFER',
      coa,
      usageType: chartOfAccountsTypes.CoaUsageTypeEnum.TRANSFER,
      name: 'Transfer',
    });
    coasByParentId.set('TRANSFER', transfers);

    const transferCoa = { type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT, id: '2' };
    transfers.push({
      id: 'BALANCE_ADJUSTMENT_2',
      coa: transferCoa,
      usageType: chartOfAccountsTypes.CoaUsageTypeEnum.TRANSFER,
      name: chartOfAccountsUtils.getBalanceAdjustmentString(transferCoa.id),
    });
  } else {
    // note: the transfer parent node is set to an external transfer
    //
    const transferCoa = { type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT, id: '2' };
    coasByParentId.get('0')?.push({
      id: 'BALANCE_ADJUSTMENT_2',
      coa: transferCoa,
      usageType: chartOfAccountsTypes.CoaUsageTypeEnum.TRANSFER,
      name: chartOfAccountsUtils.getBalanceAdjustmentString(transferCoa.id),
    });
    coasByParentId.set('BALANCE_ADJUSTMENT_2', transfers);
  }

  // add accounts to transfer node
  //
  accountsById.valueSeq().forEach((account) => {
    const coa = { type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT, id: getId(account) };
    transfers.push({
      id: chartOfAccountsUtils.getCoaId(coa),
      coa,
      usageType: chartOfAccountsTypes.CoaUsageTypeEnum.TRANSFER,
      name: `${account.name}`,
      // TODO: Should filter out unsupported account types & closed accounts
      inList: !account.isClosed && !hiddenAccountIds.contains(getId(account)),
    });
  });

  // add special node for account not found
  //
  coasByParentId.get('0')?.push({
    id: chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID,
    coa: { type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT, id: chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID },
    usageType: chartOfAccountsTypes.CoaUsageTypeEnum.TRANSFER,
    name: 'Transfer',
    inList: false,
  });
}

function createCoaNode(
  enableUsageTypes: boolean,
  categoriesByParentId: CoasByParentId,
  path: string[],
  pathName: string,
  data: CoaByParentIdObject,
  children?: Array<CoaByParentIdObject> | null,
): chartOfAccountsTypes.ChartOfAccountNode {
  const childNodes = children
    ? ImmutableList(
        children
          .filter((child) => child.id && child.id.length > 0 && child.id !== '0') // if add account fails then coa.id === '0' because of optimistic update bug
          .map((child) => {
            const innerChildren = categoriesByParentId.get(child.id!); // NOTE: we filtered out any children without ids, so we can assert that the id field is present
            return createCoaNode(
              enableUsageTypes,
              categoriesByParentId,
              [...path, data.id!],
              `${pathName}${data.name}:`,
              child,
              innerChildren,
            );
          }),
      )
    : null;
  const fullPathName = `${pathName}${data.name}`;
  const fullPathNameWithoutUsageType = enableUsageTypes
    ? fullPathName.slice(fullPathName.indexOf(':') + 1)
    : fullPathName; // If there are no usage types then we do not need to trim the front
  return chartOfAccountsTypes.ChartOfAccountNode({
    id: data.id,
    coa: data.coa
      ? chartOfAccountsTypes.mkChartOfAccount({
          id: data.coa.id,
          type: data.coa.type as chartOfAccountsTypes.ChartOfAccountProps['type'],
        })
      : null,
    usageType: data.usageType as chartOfAccountsTypes.ChartOfAccountNodeProps['usageType'],
    name: data.name,
    fullPathName,
    fullPathNameWithoutUsageType,
    inList: data.inList,
    path: ImmutableList(path),
    children: childNodes,
  });
}

const getChartOfAccountsByIdForNode = (node: chartOfAccountsTypes.ChartOfAccountNodeProps) => {
  let flattenedList = [[getId(node), node] as [string, chartOfAccountsTypes.ChartOfAccountNodeProps]];
  if (node.children) {
    flattenedList = node.children.reduce(
      (list, child) => list.concat(getChartOfAccountsByIdForNode(child)),
      flattenedList,
    );
  }
  return flattenedList;
};

export const getChartOfAccountNodesById = createSelector(
  getChartOfAccountsTree,
  (coaTree): OrderedMap<string, chartOfAccountsTypes.ChartOfAccountNodeProps> => {
    if (!coaTree || coaTree.size === 0) {
      return OrderedMap();
    }
    return OrderedMap<string, chartOfAccountsTypes.ChartOfAccountNodeProps>(
      coaTree.reduce((list, node) => list.concat(getChartOfAccountsByIdForNode(node)), ImmutableList()),
    );
  },
);

export const getAllChartOfAccountNodesById = getChartOfAccountNodesById;

export const isBill = createCachedSelector(
  (_state: unknown, _accountId: string, coa?: chartOfAccountsTypes.ChartOfAccountProps | null) =>
    coa &&
    coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
    coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT,
  (
    state: Pick<RootState, typeof CATEGORIES_REDUCER_KEY>,
    _accountId: string,
    coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  ) =>
    coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY
      ? categoriesSelectors.isExpenseCat(state, coa.id)
      : undefined,
  (state: Pick<RootState, typeof ACCOUNTS_REDUCER_KEY>, accountId: string) =>
    accountsSelectors.getAccountsById(state).get(accountId),
  (
    state: Pick<RootState, typeof ACCOUNTS_REDUCER_KEY>,
    _accountId: string,
    coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  ) =>
    coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT
      ? accountsSelectors.getAccountsById(state).get(coa.id)
      : undefined,
  (isCreditCardPayment, isExpense, accountFrom, accountTo) =>
    isCreditCardPayment ||
    isExpense ||
    (accountFrom &&
      accountTo &&
      (['CREDIT', 'LOAN'].includes(accountFrom.type) || ['CREDIT', 'LOAN'].includes(accountTo.type))),
)({
  keySelector: (_state, _accountId, coa) => `${coa?.type}:${coa && coa.id}`,
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const isCreditBill = createCachedSelector(
  (_state: unknown, _accountId: string | null | undefined, coa?: chartOfAccountsTypes.ChartOfAccountProps | null) =>
    coa &&
    coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
    coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT,
  (state: Pick<RootState, typeof ACCOUNTS_REDUCER_KEY>, accountId: string | null | undefined) =>
    accountsSelectors.getAccountsById(state).get(accountId ?? ''),
  (
    state: Pick<RootState, typeof ACCOUNTS_REDUCER_KEY>,
    _accountId: string | null | undefined,
    coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  ) =>
    coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT
      ? accountsSelectors.getAccountsById(state).get(coa.id)
      : undefined,
  (isCreditCardPayment, accountFrom, accountTo) =>
    isCreditCardPayment ||
    (accountFrom &&
      accountTo &&
      (['CREDIT', 'LOAN'].includes(accountFrom.type) || ['CREDIT', 'LOAN'].includes(accountTo.type))),
)({
  keySelector: (_state, _accountId, coa) => `${coa?.type}:${coa && coa.id}`,
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const isIncome = createSelector(
  (_state: unknown, coa?: chartOfAccountsTypes.ChartOfAccountProps | null) => coa,
  categoriesSelectors.getIncomeCOAIds,
  (coa, incomeIds) => Boolean(coa?.type === 'CATEGORY' && incomeIds.has(coa?.id)),
);

export const isIncomeOrTransfer = createSelector(
  (_state: unknown, coa?: chartOfAccountsTypes.ChartOfAccountProps | null) => coa,
  categoriesSelectors.getIncomeCOAIds,
  (coa, incomeIds) => Boolean(coa && (chartOfAccountsUtils.isTransferCOA(coa) || incomeIds.has(coa.id))),
);

type CoaStringState = Pick<RootState, typeof CATEGORIES_REDUCER_KEY | typeof ACCOUNTS_REDUCER_KEY>;

export const getCoaStringFromStateSelector = createSelector(
  (_state: unknown, coa?: chartOfAccountsTypes.ChartOfAccountProps | null) => coa,
  (_state: unknown, _coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined, noParent?: boolean) => noParent,
  (state: CoaStringState) => categoriesSelectors.getCategoriesById(state),
  (state: CoaStringState) => accountsSelectors.getAccountsById(state),
  (coa, noParent, categoriesById, accountsById): string =>
    chartOfAccountsUtils.getCoaString(coa, categoriesById, accountsById, noParent),
);

/** Legacy imperative entry point. Resolve the snapshot before entering the memoized selector. */
export function getCoaStringSelector(
  state: CoaStringState | null | undefined,
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null,
  noParent?: boolean,
): string {
  return getCoaStringFromStateSelector(state ?? store!.getState(), coa, noParent);
}

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(isBill, isCreditBill);
